// Browser fetchers — hit our /api/promotions/* routes, never the Points API
// directly (the service token is server-only).

import type {
  ApplyResponse,
  OptInStatusResponse,
  SignatureStandard,
} from "@src/features/promotions/apply/types"
import type { Drop, PublicStats } from "@src/features/promotions/types"

const REQUEST_TIMEOUT_MS = 15_000
// KYT can long-poll for 30s and retry once with a capped 2s backoff.
const OPT_IN_REQUEST_TIMEOUT_MS = 80_000

const ROUTES = {
  drops: "/api/promotions/drops",
  stats: "/api/promotions/stats",
  optIn: "/api/promotions/opt-in",
  optInStatus: "/api/promotions/opt-in/status",
} as const

export class NotAppliedError extends Error {
  constructor() {
    super("No points record for account")
    this.name = "NotAppliedError"
  }
}

export class UnauthenticatedError extends Error {
  constructor() {
    super("Not authenticated")
    this.name = "UnauthenticatedError"
  }
}

function apiUrl(path: string): string {
  const baseUrl =
    process.env.NEXT_PUBLIC_BASE_URL ??
    (typeof window !== "undefined" ? window.location.origin : "")
  return baseUrl ? new URL(path, baseUrl).toString() : path
}

type ApiRequestInit = RequestInit & { timeoutMs?: number }

async function fetchWithTimeout(path: string, init?: ApiRequestInit) {
  const { timeoutMs = REQUEST_TIMEOUT_MS, ...requestInit } = init ?? {}
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  // Abort our controller if the caller's signal (e.g. TanStack Query) aborts,
  // so a superseded refetch or unmount cancels the in-flight request.
  const caller = requestInit.signal ?? undefined
  const onCallerAbort = () => controller.abort()
  if (caller?.aborted) controller.abort()
  else caller?.addEventListener("abort", onCallerAbort, { once: true })

  try {
    return await fetch(apiUrl(path), {
      ...requestInit,
      cache: "no-store",
      signal: controller.signal,
    })
  } catch (error) {
    // Only relabel as a timeout when it wasn't the caller cancelling.
    if (controller.signal.aborted && !caller?.aborted) {
      throw new Error("Request timed out", { cause: error })
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
    caller?.removeEventListener("abort", onCallerAbort)
  }
}

async function jsonFetch<T>(path: string, signal?: AbortSignal): Promise<T> {
  const res = await fetchWithTimeout(path, { signal })
  if (!res.ok) throw new Error(`${path} → ${res.status}`)
  return (await res.json()) as T
}

export const fetchDrops = (signal?: AbortSignal) =>
  jsonFetch<Drop[]>(ROUTES.drops, signal)

export async function fetchOptInStatus(): Promise<OptInStatusResponse> {
  const res = await fetchWithTimeout(ROUTES.optInStatus)
  if (res.status === 401) throw new UnauthenticatedError()
  if (!res.ok && res.status !== 503) {
    throw new Error(`${ROUTES.optInStatus} → ${res.status}`)
  }
  return (await res.json()) as OptInStatusResponse
}

export async function prepareOptIn(): Promise<ApplyResponse> {
  return postOptIn({ action: "prepare" })
}

export async function submitOptIn({
  signedPayload,
  signatureStandard,
  signature,
}: {
  signedPayload: unknown
  signatureStandard: SignatureStandard
  signature: unknown
}): Promise<ApplyResponse> {
  return postOptIn({
    action: "submit",
    signedPayload,
    signatureStandard,
    signature,
  })
}

export async function fetchStats(signal?: AbortSignal): Promise<PublicStats> {
  const res = await fetchWithTimeout(ROUTES.stats, { signal })
  if (res.status === 401) throw new UnauthenticatedError()
  if (res.status === 404) throw new NotAppliedError()
  if (!res.ok) throw new Error(`${ROUTES.stats} → ${res.status}`)
  return (await res.json()) as PublicStats
}

async function postOptIn(body: unknown): Promise<ApplyResponse> {
  const res = await fetchWithTimeout(ROUTES.optIn, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
    timeoutMs: OPT_IN_REQUEST_TIMEOUT_MS,
  })
  if (res.status === 401) throw new UnauthenticatedError()
  const data = (await res.json()) as ApplyResponse
  if (!res.ok && !("status" in data)) {
    throw new Error(`${ROUTES.optIn} → ${res.status}`)
  }
  return data
}
